import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Mic,
  GraduationCap,
  Newspaper,
  Send,
  CheckCircle2,
  Sparkles,
  Building2,
  User,
  Mail,
  Phone,
  Linkedin,
  FileText,
  Layers,
  ArrowRight,
  AlertCircle,
  Calendar,
  MapPin,
  Clock,
  ArrowLeft,
  Info,
  Globe,
} from 'lucide-react';
import {
  applyAsSpeaker,
  submitApplication,
  Speaker,
  SpeakerApplicationPayload,
} from '../lib/api';

const TRACK_OPTIONS = [
  'Biologicals & Biofertilizers',
  'Agrochemicals & Crop Protection',
  'Agritech, AI & Precision Farming',
  'Policy, Global Trade & Market Access',
  'Post-Harvest & Cold Chain Innovations',
  'Sustainable Formulation Chemistry',
];

type ApplicationTab = 'speaker' | 'student' | 'media';

export function SpeakerApplyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTabParam = searchParams.get('tab') as ApplicationTab | null;

  const [activeTab, setActiveTab] = useState<ApplicationTab>(
    initialTabParam === 'student' || initialTabParam === 'media' ? initialTabParam : 'speaker'
  );

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'speaker' || t === 'student' || t === 'media') {
      setActiveTab(t);
    }
  }, [searchParams]);

  const handleSelectTab = (tab: ApplicationTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
    setErrorMessage(null);
  };

  // Tab 1: Speaker Proposal State
  const [formData, setFormData] = useState<SpeakerApplicationPayload>({
    fullName: '',
    email: '',
    phone: '',
    organization: '',
    designation: '',
    bio: '',
    headshotUrl: '',
    linkedinUrl: '',
    topicTitle: '',
    topicAbstract: '',
    track: TRACK_OPTIONS[0],
  });

  // Tab 2: Student Innovator Pod (POD-01..10) State
  const [studentForm, setStudentForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    university: '',
    degreeOrRole: '',
    innovationTitle: '',
    prototypeAbstract: '',
    facultyMentor: '',
    projectLink: '',
  });

  // Tab 3: Media & Press Accreditation State
  const [mediaForm, setMediaForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    publication: '',
    designation: '',
    coveragePlan: '',
    pressIdOrWebsiteUrl: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submittedSpeaker, setSubmittedSpeaker] = useState<Speaker | null>(null);
  const [submittedOtherApp, setSubmittedOtherApp] = useState<{
    id: string;
    type: 'student_innovator' | 'media';
    fullName: string;
    email: string;
    organization: string;
    titleOrBeat: string;
  } | null>(null);

  const handleSpeakerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const res = await applyAsSpeaker(formData);
      setSubmittedSpeaker(res.speaker);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to submit proposal. Please verify your details.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStudentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const res = await submitApplication({
        type: 'student_innovator',
        applicantName: studentForm.fullName.trim(),
        fullName: studentForm.fullName.trim(),
        email: studentForm.email.trim(),
        phone: studentForm.phone.trim() || undefined,
        organization: studentForm.university.trim(),
        designation: studentForm.degreeOrRole.trim(),
        roleOrTitle: studentForm.degreeOrRole.trim(),
        projectTitleOrTopic: studentForm.innovationTitle.trim(),
        abstractOrLinks: `${studentForm.prototypeAbstract.trim()}${
          studentForm.facultyMentor.trim() ? ` | Mentor: ${studentForm.facultyMentor.trim()}` : ''
        }${studentForm.projectLink.trim() ? ` | Link: ${studentForm.projectLink.trim()}` : ''}`,
        projectOrBeat: `${studentForm.innovationTitle.trim()}${
          studentForm.facultyMentor.trim() ? ` (Mentor: ${studentForm.facultyMentor.trim()})` : ''
        } — ${studentForm.prototypeAbstract.trim()}`,
        portfolioUrl: studentForm.projectLink.trim() || undefined,
      } as any);

      setSubmittedOtherApp({
        id: res.application?.id || `POD-${Date.now().toString().slice(-6)}`,
        type: 'student_innovator',
        fullName: studentForm.fullName.trim(),
        email: studentForm.email.trim(),
        organization: studentForm.university.trim(),
        titleOrBeat: studentForm.innovationTitle.trim(),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to submit Student Innovator Pod application.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMediaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const res = await submitApplication({
        type: 'media',
        applicantName: mediaForm.fullName.trim(),
        fullName: mediaForm.fullName.trim(),
        email: mediaForm.email.trim(),
        phone: mediaForm.phone.trim() || undefined,
        organization: mediaForm.publication.trim(),
        designation: mediaForm.designation.trim(),
        roleOrTitle: mediaForm.designation.trim(),
        projectTitleOrTopic: mediaForm.coveragePlan.trim(),
        abstractOrLinks: mediaForm.pressIdOrWebsiteUrl.trim() || mediaForm.coveragePlan.trim(),
        projectOrBeat: mediaForm.coveragePlan.trim(),
        portfolioUrl: mediaForm.pressIdOrWebsiteUrl.trim() || undefined,
      } as any);

      setSubmittedOtherApp({
        id: res.application?.id || `MED-${Date.now().toString().slice(-6)}`,
        type: 'media',
        fullName: mediaForm.fullName.trim(),
        email: mediaForm.email.trim(),
        organization: mediaForm.publication.trim(),
        titleOrBeat: mediaForm.coveragePlan.trim(),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to submit Media & Press Accreditation request.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // SUCCESS CONFIRMATION STATE (SPEAKER OR STUDENT POD OR MEDIA)
  if (submittedSpeaker || submittedOtherApp) {
    const isSpeaker = !!submittedSpeaker;
    const isStudent = submittedOtherApp?.type === 'student_innovator';
    const recipientName = submittedSpeaker?.fullName || submittedOtherApp?.fullName || '';
    const recipientEmail = submittedSpeaker?.email || submittedOtherApp?.email || '';
    const refId = (submittedSpeaker?.id || submittedOtherApp?.id || 'IIAB2027').slice(0, 8).toUpperCase();

    return (
      <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in zoom-in-95 duration-300">
        <div className="bg-white border border-emerald-200 rounded-3xl p-8 sm:p-12 shadow-sm text-center space-y-6">
          <div className="w-16 h-16 bg-emerald-100 border border-emerald-300 rounded-2xl mx-auto flex items-center justify-center text-[#0A3D27]">
            <CheckCircle2 className="w-10 h-10" />
          </div>

          <div className="space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#0A3D27] bg-[#0A3D27]/10 px-3 py-1 rounded-full border border-[#0A3D27]/20">
              {isSpeaker
                ? 'Speaker Proposal Under Review'
                : isStudent
                ? 'Student Innovator Pod Application Received'
                : 'Media Accreditation Request Received'}
            </span>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
              Thank You, {recipientName}!
            </h1>
            <p className="text-sm text-slate-600 max-w-lg mx-auto">
              {isSpeaker ? (
                <>
                  Your speaking proposal for <strong className="text-slate-900">IIAB Conference &amp; Expo 2027</strong>{' '}
                  has been submitted to our Technical Advisory Committee.
                </>
              ) : isStudent ? (
                <>
                  Your prototype application for the{' '}
                  <strong className="text-slate-900">Student Innovator Zone (POD-01..10)</strong> at Pavilion 3, JWCC
                  Mumbai is now queued for evaluation.
                </>
              ) : (
                <>
                  Your press accreditation request for{' '}
                  <strong className="text-slate-900">IIAB Conference &amp; Expo 2027</strong> has been routed to the
                  Media Relations Desk.
                </>
              )}
            </p>
          </div>

          {/* Submission Details Card */}
          <div className="bg-[#F8F9F6] border border-slate-200 rounded-2xl p-6 text-left space-y-3">
            <div className="flex justify-between items-center border-b border-slate-200 pb-2 text-xs">
              <span className="text-slate-500 font-semibold uppercase">Application Reference</span>
              <span className="font-mono font-bold text-[#0A3D27]">#{refId}</span>
            </div>
            <div className="flex justify-between items-center border-b border-slate-200 pb-2 text-xs">
              <span className="text-slate-500 font-semibold uppercase">
                {isSpeaker ? 'Session Title' : isStudent ? 'Innovation / Prototype' : 'Media House / Publication'}
              </span>
              <span className="font-bold text-slate-900 text-right max-w-xs truncate">
                {isSpeaker
                  ? submittedSpeaker?.topicTitle
                  : isStudent
                  ? submittedOtherApp?.titleOrBeat
                  : submittedOtherApp?.organization}
              </span>
            </div>
            <div className="flex justify-between items-center border-b border-slate-200 pb-2 text-xs">
              <span className="text-slate-500 font-semibold uppercase">Category</span>
              <span className="font-bold text-[#785613] bg-[#FAF5EA] px-2.5 py-0.5 rounded-full border border-[#E6C687]">
                {isSpeaker
                  ? submittedSpeaker?.track
                  : isStudent
                  ? 'Student Innovator Pod (POD-01..10)'
                  : 'Press & Media Pass'}
              </span>
            </div>
            <div className="flex justify-between items-center text-xs pt-1">
              <span className="text-slate-500 font-semibold uppercase">Current Status</span>
              <span className="inline-flex items-center gap-1.5 font-bold text-amber-700 bg-amber-50 px-2.5 py-0.5 rounded-full border border-amber-200">
                <Clock className="w-3 h-3" />
                Under Committee Review
              </span>
            </div>
          </div>

          {/* Notice Box */}
          <div className="bg-[#FAF5EA] border border-[#E6C687] rounded-2xl p-4 text-xs text-left text-slate-700 space-y-1.5">
            <div className="flex items-center gap-2 font-bold text-[#785613]">
              <Sparkles className="w-4 h-4" />
              <span>What Happens Next?</span>
            </div>
            <p className="leading-relaxed">
              An acknowledgment email has been dispatched to <strong>{recipientEmail}</strong>. Once approved by the
              IIAB Secretariat, you will receive your official pass credentials, 6-character Desk Collection Code, and
              direct access to the <strong>My IIAB Portal</strong>.
            </p>
          </div>

          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/#speakers"
              className="w-full sm:w-auto px-6 py-3 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Return to IIAB 2027 Overview</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => {
                setSubmittedSpeaker(null);
                setSubmittedOtherApp(null);
              }}
              className="w-full sm:w-auto px-5 py-3 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs rounded-xl border border-slate-200 transition-all cursor-pointer"
            >
              Submit Another Application
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Back link */}
      <div>
        <Link
          to="/#speakers"
          className="inline-flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-[#0A3D27] transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to IIAB 2027 Home</span>
        </Link>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-br from-[#0A3D27] via-[#0E4A30] to-[#062B1B] text-white rounded-3xl p-8 sm:p-10 overflow-hidden shadow-lg border border-emerald-800/40">
        <div className="absolute -right-12 -bottom-12 w-64 h-64 bg-[#C5A059]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative space-y-4 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-xs border border-white/20 text-[#FAF5EA] text-xs font-bold">
            <Sparkles className="w-3.5 h-3.5 text-[#C5A059]" />
            <span>Official IIAB 2027 Accreditation &amp; Call for Proposals</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight">
            IIAB 2027 Application &amp; Accreditation Hub
          </h1>

          <p className="text-sm text-emerald-100/90 leading-relaxed">
            Submit a keynote speaking proposal for the 128 PAX Conference Hall, apply for a subsidized{' '}
            <strong>Student Innovator Pod (POD-01..10)</strong>, or request official{' '}
            <strong>Media &amp; Press Accreditation</strong> for 27–28 July 2027 at Jio World Convention Centre, Mumbai.
          </p>

          <div className="flex items-center gap-6 pt-2 text-xs text-emerald-200/80 font-medium flex-wrap">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-[#C5A059]" />
              27–28 July 2027
            </span>
            <span className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-[#C5A059]" />
              JWCC Pavilion 3, BKC Mumbai
            </span>
            <span className="flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-[#C5A059]" />
              Committee-Reviewed Credentials
            </span>
          </div>
        </div>
      </div>

      {/* 3-Tab Application Selector */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-100 p-2 rounded-3xl border border-slate-200">
        <button
          type="button"
          onClick={() => handleSelectTab('speaker')}
          className={`flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-2xl text-xs font-black transition-all cursor-pointer ${
            activeTab === 'speaker'
              ? 'bg-[#0A3D27] text-[#FAF5EA] shadow-md'
              : 'bg-white/60 hover:bg-white text-slate-700'
          }`}
        >
          <Mic className="w-4 h-4" />
          <span>1. Speaker Proposal</span>
        </button>

        <button
          type="button"
          onClick={() => handleSelectTab('student')}
          className={`flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-2xl text-xs font-black transition-all cursor-pointer ${
            activeTab === 'student'
              ? 'bg-[#0A3D27] text-[#FAF5EA] shadow-md'
              : 'bg-white/60 hover:bg-white text-slate-700'
          }`}
        >
          <GraduationCap className="w-4 h-4" />
          <span>2. Student Innovator Pod</span>
        </button>

        <button
          type="button"
          onClick={() => handleSelectTab('media')}
          className={`flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-2xl text-xs font-black transition-all cursor-pointer ${
            activeTab === 'media'
              ? 'bg-[#0A3D27] text-[#FAF5EA] shadow-md'
              : 'bg-white/60 hover:bg-white text-slate-700'
          }`}
        >
          <Newspaper className="w-4 h-4" />
          <span>3. Media &amp; Press Pass</span>
        </button>
      </div>

      {errorMessage && (
        <div className="p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl text-xs flex items-start gap-3">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="leading-relaxed font-medium">{errorMessage}</div>
        </div>
      )}

      {/* ============================================================= */}
      {/* TAB 1: SPEAKER PROPOSAL FORM                                  */}
      {/* ============================================================= */}
      {activeTab === 'speaker' && (
        <form
          onSubmit={handleSpeakerSubmit}
          className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-10 shadow-xs space-y-8"
        >
          {/* SECTION 1: SPEAKER PROFILE */}
          <div className="space-y-4">
            <div className="border-b border-slate-200 pb-3 flex items-center gap-2 text-[#0A3D27]">
              <User className="w-4 h-4" />
              <h2 className="text-base font-black text-slate-900">1. Speaker Profile &amp; Credentials</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Full Name <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="e.g. Dr. Rajesh K. Sharma"
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Official Email Address <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="name@organization.com"
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Contact Phone / WhatsApp</label>
                <div className="relative">
                  <Phone className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone || ''}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="w-full pl-10 pr-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Organization / University / Institute <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    placeholder="e.g. Indian Institute of Agrochemicals"
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Designation / Job Title <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  placeholder="e.g. Head of R&D / Chief Scientific Officer"
                  required
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Speaker Biography <span className="text-rose-500">*</span>
                </label>
                <textarea
                  name="bio"
                  rows={4}
                  value={formData.bio}
                  onChange={handleChange}
                  placeholder="Provide a concise 3-4 sentence professional summary of your background, research domain, and corporate or academic achievements."
                  required
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] resize-y"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Headshot Photo URL (Optional)</label>
                <input
                  type="url"
                  name="headshotUrl"
                  value={formData.headshotUrl || ''}
                  onChange={handleChange}
                  placeholder="https://example.com/photo.jpg"
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
                <p className="text-[10px] text-slate-500 mt-1">High-resolution square headshot for website directory.</p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">LinkedIn Profile URL (Optional)</label>
                <div className="relative">
                  <Linkedin className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                  <input
                    type="url"
                    name="linkedinUrl"
                    value={formData.linkedinUrl || ''}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/username"
                    className="w-full pl-10 pr-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: PROPOSED SESSION */}
          <div className="space-y-4">
            <div className="border-b border-slate-200 pb-3 flex items-center gap-2 text-[#0A3D27]">
              <Layers className="w-4 h-4" />
              <h2 className="text-base font-black text-slate-900">2. Presentation Topic &amp; Abstract</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Conference Track / Sector <span className="text-rose-500">*</span>
                </label>
                <select
                  name="track"
                  value={formData.track}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] cursor-pointer"
                >
                  {TRACK_OPTIONS.map((track) => (
                    <option key={track} value={track}>
                      {track}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Session Topic / Working Title <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <FileText className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                  <input
                    type="text"
                    name="topicTitle"
                    value={formData.topicTitle}
                    onChange={handleChange}
                    placeholder="e.g. Advancements in Microbial Consortium Formulations for Saline Soils"
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Topic Abstract &amp; Key Takeaways <span className="text-rose-500">*</span>
                </label>
                <textarea
                  name="topicAbstract"
                  rows={5}
                  value={formData.topicAbstract}
                  onChange={handleChange}
                  placeholder="Detail the technical or business problem addressed, primary methodology, key results, and practical implications for agribusiness stakeholders (approx. 150-300 words)."
                  required
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27] resize-y"
                />
              </div>
            </div>
          </div>

          <div className="bg-[#F8F9F6] border border-slate-200 rounded-2xl p-4 text-xs text-slate-600 flex items-start gap-3">
            <Info className="w-4 h-4 text-[#0A3D27] shrink-0 mt-0.5" />
            <p className="leading-relaxed">
              Selected speakers receive full VIP Speaker conference admission, access to the VIP networking lounge, and
              access to the Speaker Hub inside My IIAB Portal to upload presentation slide decks (.pptx/.pdf).
            </p>
          </div>

          <div className="pt-2 flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto px-8 py-3.5 bg-[#0A3D27] hover:bg-[#062B1B] disabled:opacity-50 text-white font-extrabold text-xs rounded-xl shadow-md shadow-[#0A3D27]/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              {isSubmitting ? (
                <span>Submitting Proposal...</span>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Submit Speaking Proposal</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* ============================================================= */}
      {/* TAB 2: STUDENT INNOVATOR POD (POD-01..10)                     */}
      {/* ============================================================= */}
      {activeTab === 'student' && (
        <form
          onSubmit={handleStudentSubmit}
          className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-10 shadow-xs space-y-8"
        >
          <div className="bg-[#FAF5EA] border border-[#E6C687] rounded-2xl p-5 flex items-start gap-3.5">
            <GraduationCap className="w-6 h-6 text-[#785613] shrink-0 mt-0.5" />
            <div className="space-y-1 text-xs">
              <h3 className="font-black text-slate-900 text-sm">
                Student Innovator Zone — 10 Dedicated Demonstration Pods (POD-01 to POD-10)
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Agricultural universities, IIT/NIT research labs, and student agri-tech founders can apply for one of 10
                dedicated demonstration pods on the Pavilion 3 exhibition floor to showcase working prototypes, sensors,
                drones, or novel bio-formulations to global industry leaders and VCs.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="border-b border-slate-200 pb-3 flex items-center gap-2 text-[#0A3D27]">
              <GraduationCap className="w-4 h-4" />
              <h2 className="text-base font-black text-slate-900">Student Innovator &amp; Institution Details</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Lead Applicant Full Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Aarav Deshmukh"
                  value={studentForm.fullName}
                  onChange={(e) => setStudentForm({ ...studentForm, fullName: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Academic / Institutional Email <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  placeholder="aarav@university.edu.in"
                  value={studentForm.email}
                  onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Mobile / WhatsApp Number <span className="text-rose-500">*</span>
                </label>
                <input
                  type="tel"
                  required
                  placeholder="+91 98765 43210"
                  value={studentForm.phone}
                  onChange={(e) => setStudentForm({ ...studentForm, phone: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  University / Agricultural Institute <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. ICAR-IARI / IIT Bombay / TNAU"
                  value={studentForm.university}
                  onChange={(e) => setStudentForm({ ...studentForm, university: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Degree Program / Researcher Role <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Ph.D. Scholar / M.Tech Agritech"
                  value={studentForm.degreeOrRole}
                  onChange={(e) => setStudentForm({ ...studentForm, degreeOrRole: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Faculty Mentor / Lab Head (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Prof. S. V. Kulkarni"
                  value={studentForm.facultyMentor}
                  onChange={(e) => setStudentForm({ ...studentForm, facultyMentor: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Innovation / Prototype Title <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Solar-Powered Hyperspectral Pest Detection Rover"
                  value={studentForm.innovationTitle}
                  onChange={(e) => setStudentForm({ ...studentForm, innovationTitle: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Prototype Abstract &amp; Demonstration Plan <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={4}
                  required
                  placeholder="Describe your prototype, TRL level, field validation results, and what hardware/samples you plan to display at your Student Innovator Pod..."
                  value={studentForm.prototypeAbstract}
                  onChange={(e) => setStudentForm({ ...studentForm, prototypeAbstract: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Pitch Deck / Demo Video / GitHub Link (Optional)
                </label>
                <div className="relative">
                  <Globe className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                  <input
                    type="url"
                    placeholder="https://..."
                    value={studentForm.projectLink}
                    onChange={(e) => setStudentForm({ ...studentForm, projectLink: e.target.value })}
                    className="w-full pl-10 pr-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto px-8 py-3.5 bg-[#0A3D27] hover:bg-[#062B1B] disabled:opacity-50 text-white font-extrabold text-xs rounded-xl shadow-md shadow-[#0A3D27]/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              {isSubmitting ? (
                <span>Submitting Application...</span>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Apply for Student Innovator Pod</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* ============================================================= */}
      {/* TAB 3: MEDIA & PRESS ACCREDITATION                            */}
      {/* ============================================================= */}
      {activeTab === 'media' && (
        <form
          onSubmit={handleMediaSubmit}
          className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-10 shadow-xs space-y-8"
        >
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex items-start gap-3.5">
            <Newspaper className="w-6 h-6 text-[#0A3D27] shrink-0 mt-0.5" />
            <div className="space-y-1 text-xs">
              <h3 className="font-black text-slate-900 text-sm">
                Official Press &amp; Media Accreditation — IIAB 2027
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Accredited journalists, agricultural trade editors, and broadcast crews receive complimentary Media
                Badges with access to the Pavilion 3 Media Lounge, Press Conference briefings, and 1-on-1 speaker
                interview rooms.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="border-b border-slate-200 pb-3 flex items-center gap-2 text-[#0A3D27]">
              <Newspaper className="w-4 h-4" />
              <h2 className="text-base font-black text-slate-900">Journalist &amp; Publication Credentials</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Journalist Full Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Meera Krishnan"
                  value={mediaForm.fullName}
                  onChange={(e) => setMediaForm({ ...mediaForm, fullName: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Editorial Email Address <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  placeholder="meera@agripublishing.com"
                  value={mediaForm.email}
                  onChange={(e) => setMediaForm({ ...mediaForm, email: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Direct Phone / WhatsApp <span className="text-rose-500">*</span>
                </label>
                <input
                  type="tel"
                  required
                  placeholder="+91 98765 43210"
                  value={mediaForm.phone}
                  onChange={(e) => setMediaForm({ ...mediaForm, phone: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Media House / Publication Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. AgroSpectrum India / Krishi Jagran"
                  value={mediaForm.publication}
                  onChange={(e) => setMediaForm({ ...mediaForm, publication: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Editorial Role / Designation <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Senior Correspondent / Editor-in-Chief"
                  value={mediaForm.designation}
                  onChange={(e) => setMediaForm({ ...mediaForm, designation: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Publication Website / Press Card Link <span className="text-rose-500">*</span>
                </label>
                <input
                  type="url"
                  required
                  placeholder="https://..."
                  value={mediaForm.pressIdOrWebsiteUrl}
                  onChange={(e) => setMediaForm({ ...mediaForm, pressIdOrWebsiteUrl: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Beat &amp; Planned Coverage for IIAB 2027 <span className="text-rose-500">*</span>
                </label>
                <textarea
                  rows={4}
                  required
                  placeholder="Describe your editorial beat, planned articles or video interviews, and any specific keynote speakers or exhibitors you wish to interview..."
                  value={mediaForm.coveragePlan}
                  onChange={(e) => setMediaForm({ ...mediaForm, coveragePlan: e.target.value })}
                  className="w-full px-4 py-2.5 bg-[#F8F9F6] border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto px-8 py-3.5 bg-[#0A3D27] hover:bg-[#062B1B] disabled:opacity-50 text-white font-extrabold text-xs rounded-xl shadow-md shadow-[#0A3D27]/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              {isSubmitting ? (
                <span>Submitting Request...</span>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Request Media Accreditation</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default SpeakerApplyPage;
